import mapboxgl from 'mapbox-gl';
import * as polygonClipping from 'polygon-clipping';

/**
 * Reconstruct a building's COMPLETE footprint from the currently-rendered
 * `building` tiles by feature id. A building spanning tile boundaries is split
 * into one slice per tile, all sharing the id; collect every rendered slice and
 * union to dissolve the internal seams. Returns null when no slices are loaded
 * (low zoom / footprint off-screen) — the caller then falls back to the backend.
 * This is the fast, no-network path: when the footprint is on screen, we already
 * have the polygon, so we render it directly instead of calling /building-geometry.
 */
export function footprintFromRenderedMap(
  map: mapboxgl.Map,
  featureId: string | number,
  fallbackGeometry?: GeoJSON.Geometry | null,
): GeoJSON.Polygon | GeoJSON.MultiPolygon | null {
  // Collect whole polygons (each = [outerRing, ...innerRings]) so courtyard
  // holes survive the union rather than being filled in.
  const polygons: number[][][][] = [];
  try {
    const slices = map.queryRenderedFeatures({
      layers: ['building'],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter: ['==', ['id'], featureId as any],
    });
    slices.forEach((slice) => {
      if (slice.geometry?.type === 'Polygon') {
        polygons.push(slice.geometry.coordinates as number[][][]);
      } else if (slice.geometry?.type === 'MultiPolygon') {
        (slice.geometry.coordinates as number[][][][]).forEach((poly) => {
          polygons.push(poly);
        });
      }
    });
  } catch (err) {
    // ignore failures when querying rendered features
  }

  if (polygons.length === 0 && fallbackGeometry?.type === 'Polygon') {
    polygons.push((fallbackGeometry as GeoJSON.Polygon).coordinates as number[][][]);
  }
  if (polygons.length === 0) return null;

  // Union slices to dissolve tile-split seams (holes preserved).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let buildingGeometry: any;
  try {
    const unionResult: number[][][][] = polygonClipping.union(
      ...(polygons as [polygonClipping.Polygon, ...polygonClipping.Polygon[]]),
    );
    buildingGeometry =
      unionResult.length === 1
        ? { type: 'Polygon', coordinates: unionResult[0] }
        : { type: 'MultiPolygon', coordinates: unionResult };
  } catch (err) {
    buildingGeometry =
      polygons.length > 1
        ? { type: 'MultiPolygon', coordinates: polygons }
        : { type: 'Polygon', coordinates: polygons[0] };
  }

  // Validate coordinates — malformed data can crash WebGL.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isFiniteNumber = (n: any) => typeof n === 'number' && Number.isFinite(n);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isValidLngLat = (lng: any, lat: any) =>
    isFiniteNumber(lng) && isFiniteNumber(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
  try {
    const polygons =
      buildingGeometry.type === 'Polygon' ? [buildingGeometry.coordinates] : buildingGeometry.coordinates;
    for (const poly of polygons) {
      for (const ring of poly) {
        for (const pair of ring) {
          if (!isValidLngLat(pair[0], pair[1])) return null;
        }
      }
    }
  } catch {
    return null;
  }
  return buildingGeometry;
}
